import React, { useEffect, useState } from "react";

import {
  useGetFootprintingBeefPrimaryDataById,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingDairyPrimaryDataById,
  useGetFootprintingFarmPrimaryDataById,
  useGetFootprintingPigletPrimaryDataById,
  useGetFootprintingPorkPrimaryDataById,
  useMutateFootprintingBeefPrimaryData,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingDairyPrimaryData,
  useMutateFootprintingFarmPrimaryData,
  useMutateFootprintingPigletPrimaryData,
  useMutateFootprintingPorkPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_FOOTPRINT_ASSESSMENT_TYPE } from "consts";

import { Card, Flex, Form, PageSpin, TextArea, Typography } from "components";

const MAX_COMMENT_LENGTH = 2000;

interface FormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId?: string;
  year: number;
}

type CommentFormProps = { formType: ENUM_FOOTPRINT_ASSESSMENT_TYPE } & FormProps;

const CommentFormUI = ({
  formType,
  comment,
  initialComment,
  isLoading,
  readOnly,
  onCommentChange,
  onSave,
}: {
  formType: ENUM_FOOTPRINT_ASSESSMENT_TYPE;
  comment: string | null;
  initialComment: string | null;
  isLoading: boolean;
  readOnly?: boolean;
  onCommentChange: (comment: string | null) => void;
  onSave: (args?: never) => void;
}) => {
  const { t } = useTranslation("footprinting");

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) onSave();
    },
    [isLoading, onSave, readOnly]
  );

  if (isLoading) return <PageSpin />;

  return (
    <Card border dataTestId={`footprinting-${formType}-comment-form`} className="padding-200">
      <Form key={initialComment ?? "empty"}>
        <Flex vertical gap={16}>
          <TextArea
            name="comment"
            label={t("commentForm.label")}
            placeholder={t("commentForm.placeholder")}
            dataTestId={`footprinting-${formType}-comment-textarea`}
            disabled={readOnly}
            maxLength={MAX_COMMENT_LENGTH}
            rows={6}
            autoSize={{ minRows: 10, maxRows: 20 }}
            initialValue={initialComment ?? ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const val = e.target.value || "";
              onCommentChange(val || null);
            }}
          />
          <Typography variant="paragraph-s" className="text-right color-text-secondary">
            {comment?.length ?? 0} / {MAX_COMMENT_LENGTH}
          </Typography>
        </Flex>
      </Form>
    </Card>
  );
};

const useComment = (queryComment: string | null): [string | null, React.Dispatch<React.SetStateAction<string | null>>] => {
  const [comment, setComment] = useState<string | null>(null);

  useEffect(() => {
    if (queryComment !== undefined) setComment(queryComment ?? null);
  }, [queryComment]);

  return [comment, setComment];
};

const BeefForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => {
  const { data, isLoading } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const [comment, setComment] = useComment(data?.comment ?? null);
  const { mutate } = useMutateFootprintingBeefPrimaryData(farmId, year, { comment, farmId, year }, primaryDataId);

  return (
    <CommentFormUI
      formType="beef"
      comment={comment}
      initialComment={data?.comment ?? null}
      isLoading={!!primaryDataId && isLoading}
      readOnly={readOnly}
      onCommentChange={setComment}
      onSave={mutate}
    />
  );
};

const CropForm = ({ primaryDataId, readOnly, farmId, cropId = "", year }: FormProps) => {
  const { data, isLoading } = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");
  const [comment, setComment] = useComment(data?.comment ?? null);
  const { mutate } = useMutateFootprintingCropPrimaryData(farmId, cropId, year, { comment, farmId, cropId, year }, primaryDataId);

  return (
    <CommentFormUI
      formType="crop"
      comment={comment}
      initialComment={data?.comment ?? null}
      isLoading={!!primaryDataId && isLoading}
      readOnly={readOnly}
      onCommentChange={setComment}
      onSave={mutate}
    />
  );
};

const DairyForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => {
  const { data, isLoading } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const [comment, setComment] = useComment(data?.comment ?? null);
  const { mutate } = useMutateFootprintingDairyPrimaryData(farmId, year, { comment, farmId, year }, primaryDataId);

  return (
    <CommentFormUI
      formType="dairy"
      comment={comment}
      initialComment={data?.comment ?? null}
      isLoading={!!primaryDataId && isLoading}
      readOnly={readOnly}
      onCommentChange={setComment}
      onSave={mutate}
    />
  );
};

const PigletForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => {
  const { data, isLoading } = useGetFootprintingPigletPrimaryDataById(primaryDataId ?? "");
  const [comment, setComment] = useComment(data?.comment ?? null);
  const { mutate } = useMutateFootprintingPigletPrimaryData(farmId, year, { comment, farmId, year }, primaryDataId);

  return (
    <CommentFormUI
      formType="piglet"
      comment={comment}
      initialComment={data?.comment ?? null}
      isLoading={!!primaryDataId && isLoading}
      readOnly={readOnly}
      onCommentChange={setComment}
      onSave={mutate}
    />
  );
};

const PorkForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => {
  const { data, isLoading } = useGetFootprintingPorkPrimaryDataById(primaryDataId ?? "");
  const [comment, setComment] = useComment(data?.comment ?? null);
  const { mutate } = useMutateFootprintingPorkPrimaryData(farmId, year, { comment, farmId, year }, primaryDataId);

  return (
    <CommentFormUI
      formType="pork"
      comment={comment}
      initialComment={data?.comment ?? null}
      isLoading={!!primaryDataId && isLoading}
      readOnly={readOnly}
      onCommentChange={setComment}
      onSave={mutate}
    />
  );
};

const FarmForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => {
  const { data, isLoading } = useGetFootprintingFarmPrimaryDataById(primaryDataId ?? "");
  const [comment, setComment] = useComment(data?.comment ?? null);
  const { mutate } = useMutateFootprintingFarmPrimaryData(farmId, year, { comment, farmId, year }, primaryDataId);

  return (
    <CommentFormUI
      formType="farm"
      comment={comment}
      initialComment={data?.comment ?? null}
      isLoading={!!primaryDataId && isLoading}
      readOnly={readOnly}
      onCommentChange={setComment}
      onSave={mutate}
    />
  );
};

const CommentForm = ({ formType, primaryDataId, readOnly, farmId, year, ...rest }: CommentFormProps) => {
  switch (formType) {
    case "beef":
      return <BeefForm primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />;
    case "crop":
      return <CropForm primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} cropId={(rest as { cropId: string }).cropId} year={year} />;
    case "dairy":
      return <DairyForm primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />;
    case "piglet":
      return <PigletForm primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />;
    case "pork":
      return <PorkForm primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />;
    case "farm":
      return <FarmForm primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />;
    default:
      return null;
  }
};

const BeefCommentForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => (
  <CommentForm formType="beef" primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />
);

BeefCommentForm.Key = "beef.comment";
export { BeefCommentForm };

const FarmCommentForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => (
  <CommentForm formType="farm" primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />
);

FarmCommentForm.Key = "farm.comment";
export { FarmCommentForm };

const CropCommentForm = ({ primaryDataId, readOnly, farmId, cropId, year }: FormProps) => (
  <CommentForm formType="crop" primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} cropId={cropId} year={year} />
);

CropCommentForm.Key = "crop.comment";
export { CropCommentForm };

const DairyCommentForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => (
  <CommentForm formType="dairy" primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />
);

DairyCommentForm.Key = "dairy.comment";
export { DairyCommentForm };

const PorkCommentForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => (
  <CommentForm formType="pork" primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />
);

PorkCommentForm.Key = "pork.comment";
export { PorkCommentForm };

const PigletCommentForm = ({ primaryDataId, readOnly, farmId, year }: FormProps) => (
  <CommentForm formType="piglet" primaryDataId={primaryDataId} readOnly={readOnly} farmId={farmId} year={year} />
);

PigletCommentForm.Key = "piglet.comment";
export { PigletCommentForm };
